import { ChangeEvent, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { HOME_BOARD_SECTIONS } from '@/containers/SubscriptionFormPageNew/Components/CommunicationBoard/constants'
import { communicationBoardModuleActions } from '@/containers/SubscriptionFormPageNew/Components/CommunicationBoard/CommunicationBoardSagas'
import useCommunicationBoardEventListener from '@/containers/SubscriptionFormPageNew/Components/CommunicationBoard/useCommunicationBoardEventListener'
import { selectCommunicationBoardState } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'
import { selectHomeMessagesExpanded, selectHomeMessagesSearch } from '@/containers/HomePage/HomeSelectors'
import { setHomeMessagesSearch, toggleHomeMessage } from '@/containers/HomePage/HomeSlice'
import { buildSectionView, splitBoardSection } from './organizeHomeMessages'
import { HomeMessage, HomeMessageSection } from './types'

type BoardSection = { section: string; content: string | null }
type BoardState = { sections?: BoardSection[]; error?: string | null }

const contentOf = (sections: BoardSection[], key: string) =>
  sections.find(({ section }) => section === key)?.content ?? ''

const useHomeMessages = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { sections = [], error = null } = useSelector(selectCommunicationBoardState) as BoardState
  const search = useSelector(selectHomeMessagesSearch) as string
  const expanded = useSelector(selectHomeMessagesExpanded) as Record<HomeMessageSection, string | null>

  useEffect(() => {
    dispatch({ type: communicationBoardModuleActions.COMMUNICATION_BOARD_MODULE_LOAD_DATA })
  }, [])
  useCommunicationBoardEventListener()

  const welcomeMessages = useMemo(
    () => splitBoardSection(contentOf(sections, HOME_BOARD_SECTIONS.WELCOME), 'welcome'),
    [sections],
  )
  const scriptMessages = useMemo(
    () => splitBoardSection(contentOf(sections, HOME_BOARD_SECTIONS.SCRIPTS), 'scripts'),
    [sections],
  )
  const welcome = buildSectionView(welcomeMessages, search, expanded.welcome)
  const scripts = buildSectionView(scriptMessages, search, expanded.scripts)

  const onSearch = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    dispatch(setHomeMessagesSearch(event.currentTarget.value))
  }

  const onToggle = ({ section, id }: HomeMessage) => {
    dispatch(toggleHomeMessage({ section, id }))
  }

  return {
    t,
    error,
    nothingToShow: !error && welcome.isEmpty && scripts.isEmpty,
    search,
    welcome,
    scripts,
    labels: {
      close: t('common:close'),
      untitled: t('home:messages.untitled'),
    },
    onSearch,
    onToggle,
  }
}

export default useHomeMessages
